/**
 * @fileoverview Página para exportar informe de reservas de inventario a Excel
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { RepositoryFactory } from '../../repositories';
import { generateExcel } from '../../helpers/informeReservaInventarioExcelHelper';
import { formatProductoNombre } from '../../utils/productoInventarioHelper';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

const InformeReservaInventarioPage = () => {
  document.title = 'Informe Reservas Inventario | GraphicForce - Printesco';

  // Estados de filtros
  const [filters, setFilters] = useState({
    fechaCreacion: { inicio: null, fin: null },
    totalMin: null,
    totalMax: null,
    usuario: null,
    orden: null,
    producto: null,
    tipo: null, // 'devolucion', 'solicitud', 'ambas', null
  });

  // Estados de validación
  const [dateErrors, setDateErrors] = useState({
    fechaCreacion: null,
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [ordenes, setOrdenes] = useState([]);
  const [loadingOrdenes, setLoadingOrdenes] = useState(true);
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(true);

  // Repositorios
  const reservaRepository = useMemo(() => RepositoryFactory.getRepository('reserva_inventario'), []);
  const usuarioRepository = useMemo(() => RepositoryFactory.getRepository('users'), []);
  const ordenRepository = useMemo(() => RepositoryFactory.getRepository('orden_produccion'), []);
  const productoInventarioRepository = useMemo(() => RepositoryFactory.getRepository('producto_inventario'), []);

  // Cargar usuarios al montar
  useEffect(() => {
    const loadUsuarios = async () => {
      try {
        setLoadingUsuarios(true);
        const usuariosData = await usuarioRepository.getOperariosParaAutocompletado();
        setUsuarios(usuariosData.map(u => {
          const nombre = u.name || u.email || 'Sin nombre';
          const estaActivo = u.activo !== false; // Default to true if undefined
          const label = estaActivo ? nombre : `${nombre} (desactivado)`;
          return { 
            value: u.id, 
            label: label
          };
        }));
      } catch (err) {
        console.error('Error loading usuarios:', err);
        setError('Error al cargar usuarios');
      } finally {
        setLoadingUsuarios(false);
      }
    };

    loadUsuarios();
  }, [usuarioRepository]);

  // Cargar órdenes al montar
  useEffect(() => {
    const loadOrdenes = async () => {
      try {
        setLoadingOrdenes(true);
        const ordenesData = await ordenRepository.getOrdenesParaAutocompletado();
        setOrdenes(ordenesData.map(o => ({ 
          value: o.id, 
          label: `Orden ${o.numero}` 
        })));
      } catch (err) {
        console.error('Error loading ordenes:', err);
        setError('Error al cargar órdenes');
      } finally {
        setLoadingOrdenes(false);
      }
    };

    loadOrdenes();
  }, [ordenRepository]);

  // Cargar productos de inventario al montar
  useEffect(() => {
    const loadProductos = async () => {
      try {
        setLoadingProductos(true);
        const productosData = await productoInventarioRepository.getAllActive();
        setProductos(productosData.map(p => ({ 
          value: p.id, 
          label: formatProductoNombre(p)
        })));
      } catch (err) {
        console.error('Error loading productos:', err);
        setError('Error al cargar productos de inventario');
      } finally {
        setLoadingProductos(false);
      }
    };

    loadProductos();
  }, [productoInventarioRepository]);

  // Validar filtros activos
  const hasFilters = useMemo(() => {
    return (
      (filters.fechaCreacion.inicio || filters.fechaCreacion.fin) ||
      filters.totalMin !== null ||
      filters.totalMax !== null ||
      filters.usuario !== null ||
      filters.orden !== null ||
      filters.producto !== null ||
      filters.tipo !== null
    );
  }, [filters]);

  // Handler para fechas
  const handleDateChange = (rangeType, date) => {
    const dateValue = date ? (Array.isArray(date) ? date[0] : date) : null;
    const dateString = dateValue ? new Date(dateValue).toISOString().split('T')[0] : null;

    setFilters(prev => {
      const newFilters = {
        ...prev,
        fechaCreacion: {
          ...prev.fechaCreacion,
          [rangeType]: dateString,
        },
      };

      // Validar rango de fechas (máximo 365 días)
      const inicio = newFilters.fechaCreacion.inicio;
      const fin = newFilters.fechaCreacion.fin;

      if (inicio && fin) {
        const inicioDate = new Date(inicio);
        const finDate = new Date(fin);
        const diffDays = Math.ceil((finDate - inicioDate) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
          setDateErrors(prev => ({
            ...prev,
            fechaCreacion: 'La fecha de fin debe ser posterior a la fecha de inicio',
          }));
        } else if (diffDays > 365) {
          setDateErrors(prev => ({
            ...prev,
            fechaCreacion: 'El rango de fechas no puede exceder 365 días',
          }));
        } else {
          setDateErrors(prev => ({
            ...prev,
            fechaCreacion: null,
          }));
        }
      } else {
        setDateErrors(prev => ({
          ...prev,
          fechaCreacion: null,
        }));
      }

      return newFilters;
    });

    setError(null);
  };

  // Handler para usuario
  const handleUsuarioChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      usuario: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para orden
  const handleOrdenChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      orden: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para producto
  const handleProductoChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      producto: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para tipo
  const handleTipoChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      tipo: value === '' ? null : value,
    }));
    setError(null);
  };

  // Handler para total mínimo
  const handleTotalMinChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      totalMin: value === '' ? null : parseFloat(value),
    }));
    setError(null);
  };

  // Handler para total máximo
  const handleTotalMaxChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      totalMax: value === '' ? null : parseFloat(value),
    }));
    setError(null);
  };

  // Handler para exportar
  const handleExport = async () => {
    // Validar que no haya errores de fecha
    const hasDateErrors = Object.values(dateErrors).some(err => err !== null);
    if (hasDateErrors) {
      setError('Por favor, corrija los errores en los rangos de fechas antes de exportar.');
      return;
    }

    // Validar que haya al menos un filtro activo
    if (!hasFilters) {
      setError('Por favor, seleccione al menos un filtro antes de exportar.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Preparar filtros para el repositorio
      const reporteFilters = {
        fechaInicio: filters.fechaCreacion,
        totalMin: filters.totalMin,
        totalMax: filters.totalMax,
        usuario: filters.usuario,
        orden: filters.orden,
        producto: filters.producto,
        tipo: filters.tipo,
      };

      // Obtener reservas
      const reservas = await reservaRepository.getReservasParaReporte(reporteFilters);

      if (reservas.length === 0) {
        setError('No se encontraron reservas con los filtros seleccionados.');
        setLoading(false);
        return;
      }

      // Generar Excel
      generateExcel(reservas);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Error al generar el reporte. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs maintitle="Dashboard" title="Exportación" breadcrumbItem="Informe Reservas Inventario" />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Informe de Reservas de Inventario</h4>
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {!hasFilters && (
                  <Alert color="info" className="mb-3">
                    Por favor, seleccione al menos un filtro para continuar.
                  </Alert>
                )}

                {dateErrors.fechaCreacion && (
                  <Alert color="warning" className="mb-3">
                    {dateErrors.fechaCreacion}
                  </Alert>
                )}

                <Form>
                  <Row>
                    {/* Fecha de Creación */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="fechaCreacionInicio">Fecha de Creación - Inicio</Label>
                        <Flatpickr
                          id="fechaCreacionInicio"
                          className="form-control"
                          placeholder="Seleccionar fecha inicio"
                          value={filters.fechaCreacion.inicio || ''}
                          onChange={(date) => handleDateChange('inicio', date)}
                          options={{
                            dateFormat: 'Y-m-d',
                            maxDate: filters.fechaCreacion.fin || undefined,
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="fechaCreacionFin">Fecha de Creación - Fin</Label>
                        <Flatpickr
                          id="fechaCreacionFin"
                          className="form-control"
                          placeholder="Seleccionar fecha fin"
                          value={filters.fechaCreacion.fin || ''}
                          onChange={(date) => handleDateChange('fin', date)}
                          options={{
                            dateFormat: 'Y-m-d',
                            minDate: filters.fechaCreacion.inicio || undefined,
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Total Mínimo */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="totalMin">Total Mínimo</Label>
                        <Input
                          type="number"
                          id="totalMin"
                          placeholder="Total mínimo"
                          value={filters.totalMin || ''}
                          onChange={handleTotalMinChange}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                    </Col>
                    {/* Total Máximo */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="totalMax">Total Máximo</Label>
                        <Input
                          type="number"
                          id="totalMax"
                          placeholder="Total máximo"
                          value={filters.totalMax || ''}
                          onChange={handleTotalMaxChange}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Usuario */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="usuario">Usuario</Label>
                        <Select
                          id="usuario"
                          options={usuarios}
                          isClearable
                          isSearchable
                          placeholder={loadingUsuarios ? 'Cargando...' : 'Seleccionar usuario'}
                          isLoading={loadingUsuarios}
                          value={usuarios.find(u => u.value === filters.usuario) || null}
                          onChange={handleUsuarioChange}
                        />
                      </FormGroup>
                    </Col>

                    {/* Orden de Producción */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="orden">Orden de Producción</Label>
                        <Select
                          id="orden"
                          options={ordenes}
                          isClearable
                          isSearchable
                          placeholder={loadingOrdenes ? 'Cargando...' : 'Seleccionar orden'}
                          isLoading={loadingOrdenes}
                          value={ordenes.find(o => o.value === filters.orden) || null}
                          onChange={handleOrdenChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Producto de Inventario */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="producto">Producto de Inventario</Label>
                        <Select
                          id="producto"
                          options={productos}
                          isClearable
                          isSearchable
                          placeholder={loadingProductos ? 'Cargando...' : 'Seleccionar producto'}
                          isLoading={loadingProductos}
                          value={productos.find(p => p.value === filters.producto) || null}
                          onChange={handleProductoChange}
                        />
                      </FormGroup>
                    </Col>

                    {/* Tipo */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="tipo">Tipo</Label>
                        <Input
                          type="select"
                          id="tipo"
                          value={filters.tipo || ''}
                          onChange={handleTipoChange}
                        >
                          <option value="">Todas</option>
                          <option value="devolucion">Devolución</option>
                          <option value="solicitud">Solicitud</option>
                          <option value="ambas">Ambas</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Botón Exportar */}
                  <FormGroup>
                    <Button
                      color="primary"
                      onClick={handleExport}
                      disabled={!hasFilters || loading || Object.values(dateErrors).some(err => err !== null)}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        'Exportar a Excel'
                      )}
                    </Button>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InformeReservaInventarioPage;

